import { LoadingButton } from "@mui/lab";
import { Box, Grid, Theme, Typography, lighten } from "@mui/material";

import { PackageEvent } from "./PackageEvent";
import { Divider } from "@/shared/base/components/Divider/Divider";

import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type { EventType } from "@/global/store/data/types";

interface Props {
  title: string;
  description: string;
  events: EventType[];
  isLoading?: boolean;
  isSelected?: boolean;
  onClick?: (a: any) => void;
  onlyOnePackageAvailable?: boolean;
}

export function PackageCard(props: Props) {
  const { theme } = useAppSystem();
  const styles = useStyles(theme);
  const { sm: smDown } = useCssMediaQuery("down");

  return (
    <Box sx={styles.root} onClick={props?.onClick}>
      <Box p={smDown ? 3 : 4}>
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize={"1.2rem"}
          color="text.primary"
          lineHeight={1.2}
        >
          {props.title}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
          {props.description}
        </Typography>
      </Box>
      <Divider>
        <Box
          px={smDown ? 3 : 4}
          sx={{ ...styles.buttonWrapper, opacity: props?.isLoading ? 1 : 0 }}
          className="PackageCard__buttonWrapper"
        >
          <LoadingButton
            variant={"contained"}
            size="medium"
            fullWidth
            sx={{
              fontSize: "0.9rem",
              bgcolor: props?.isLoading
                ? "rgb(204, 210, 217) !important"
                : "primary.main",
            }}
            loading={props?.isLoading}
          >
            Select +
          </LoadingButton>
        </Box>
      </Divider>
      <Box p={smDown ? 3 : 4} pt={5}>
        <Grid container spacing={3}>
          {props.events.map((evt, idx) => {
            return (
              <Grid
                key={evt.eventId}
                item
                xs={12}
                md={props?.onlyOnePackageAvailable ? 6 : 12}
                lg={props?.onlyOnePackageAvailable ? 6 : 12}
              >
                <PackageEvent
                  logoUrl={evt.logoUrl}
                  title={evt.title}
                  date={evt.date}
                  time={evt.time}
                  tag={evt.tag ? evt.tag : ""}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}

function useStyles(theme: Theme) {
  // colors
  const dividerColor = theme.palette.divider;
  const hoverShadowColor = lighten(theme.palette.primary.main, 0.8);

  return {
    root: {
      width: "100%",
      height: "100%",
      backgroundColor: theme.palette.background.paper,
      borderRadius: "8px",
      outline: `1px solid ${dividerColor}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      transformOrigin: "top top",

      "&:hover": {
        transform: `scale(1.02)`,
        boxShadow: `0 0 0 6px ${hoverShadowColor}`,

        "& .PackageCard__buttonWrapper": {
          opacity: 1,
        },
      },
    },

    buttonWrapper: {
      transition: "all 0.3s ease",

      [theme.breakpoints.down("md")]: {
        opacity: 1,
      },
    },
  };
}
